import { useId } from 'react';
import type { FeedState } from '../lib/feeds';
import { fixtureIsUpcoming, fixtureOrderTime } from '../lib/matchday';
import type { Fixture, FixturesFeed, MatchFeed } from '../lib/types';
import { featuredMatchdayFixture, fixtureState, matchdayKickoffText, matchdayLiveConfirmed, matchScore } from './MatchdaySection';
import './home-fixtures.css';

const OFFICIAL_CALENDAR = 'https://www.arsenal.com/fixtures/men/fixtures';

export interface HomeFixturesProps {
  fixtures: FeedState<FixturesFeed>;
  match?: FeedState<MatchFeed>;
  onMatch: (id: string) => void;
  onFullBook: () => void;
  now?: number;
}

function orderedTime(fixture: Fixture): number {
  const time = fixtureOrderTime(fixture);
  return Number.isFinite(time) ? time : 0;
}

/** A short window into the existing feed, with no requests or polling of its own. */
export function homeFixtureWindow(fixtures: FeedState<FixturesFeed>, match?: FeedState<MatchFeed>, now = Date.now()): Fixture[] {
  const all = fixtures.data.fixtures;
  const feature = featuredMatchdayFixture(fixtures, match, now);
  const live = all.find((fixture) => matchdayLiveConfirmed(fixture, fixtures, match)) ??
    all.find((fixture) => fixture.status === 'live' && fixture.id === match?.data.matchId) ??
    all.find((fixture) => fixture.status === 'live');
  const next = all.filter((fixture) => fixture.status === 'scheduled' && fixtureIsUpcoming(fixture, now))
    .sort((a, b) => orderedTime(a) - orderedTime(b)).slice(0, 3);
  const recent = all.filter((fixture) => fixture.status === 'ft')
    .sort((a, b) => orderedTime(b) - orderedTime(a)).slice(0, 2);
  const unique = new Map<string, Fixture>();
  for (const fixture of [feature, live, ...next, ...recent]) if (fixture) unique.set(fixture.id, fixture);
  if (!unique.size && all.length) {
    // A postponed or overdue-only calendar still deserves an honest visible row.
    const pending = [...all].sort((a, b) => Math.abs(orderedTime(a) - now) - Math.abs(orderedTime(b) - now))[0];
    unique.set(pending.id, pending);
  }
  return [...unique.values()];
}

function safeSource(value?: string): string | null {
  try { return value && new URL(value).protocol === 'https:' ? value : null; } catch { return null; }
}

export function HomeFixtures({ fixtures, match, onMatch, onFullBook, now = Date.now() }: HomeFixturesProps) {
  const headingId = useId();
  const rows = homeFixtureWindow(fixtures, match, now);
  const feature = featuredMatchdayFixture(fixtures, match, now);
  const source = fixtures.data.source;
  const sourceUrl = safeSource(source?.url);
  const licenseUrl = safeSource(source?.licenseUrl);
  const checkedAt = source?.checkedAt ?? fixtures.asOf ?? fixtures.data.editorial?.checkedAt;
  const checked = checkedAt && Number.isFinite(Date.parse(checkedAt))
    ? new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(checkedAt)) : null;
  const coverage = fixtures.data.editorial?.coverage === 'premier-league-complete' ? 'Men’s Premier League only.' : 'Selected men’s fixtures.';

  return <section className="rt-home-fixtures" aria-labelledby={headingId}>
    <header className="rt-home-fixtures__heading">
      <div><span className="rt-home-fixtures__eyebrow">The next chapter</span><h2 id={headingId}>The matchbook.</h2></div>
      <button type="button" onClick={onFullBook}>Full fixture book <span aria-hidden="true">↗</span></button>
    </header>
    <p className="rt-home-fixtures__status"><strong>{fixtures.stale ? 'Saved calendar' : fixtures.data.provisional ? 'Provisional calendar' : source?.status === 'degraded' ? 'Provider check delayed' : 'Published calendar'}</strong> · {coverage}{checked ? ` Checked ${checked}.` : ''}{fixtures.stale ? ' Dates may have changed.' : ''}</p>
    {rows.length ? <>
      <ol className="rt-home-fixtures__rail" aria-label="Recent results and upcoming fixtures">
        {rows.map((fixture) => {
          const score = matchScore(fixture, fixtures, match);
          const state = fixtureState(fixture, fixtures, match, now);
          const live = matchdayLiveConfirmed(fixture, fixtures, match);
          const label = live ? 'On the pitch' : fixture.status === 'ft' ? 'In the book' : fixture.status === 'postponed' ? 'Fixture update' : fixtureIsUpcoming(fixture, now) ? 'Up next' : 'Awaiting an update';
          const venue = fixture.venue === 'H' ? 'Home' : fixture.venue === 'A' ? 'Away' : 'Neutral';
          return <li key={fixture.id} className={fixture.id === feature?.id ? 'rt-home-fixtures__featured' : undefined}>
            <button type="button" onClick={() => onMatch(fixture.id)}>
              <span className="rt-home-fixtures__card-top"><span>{label}</span><span>{venue}</span></span>
              <strong className="rt-home-fixtures__tie">Arsenal <span>{fixture.venue === 'A' ? 'at' : 'v'}</span><br/>{fixture.opponent}</strong>
              {score && <b className="rt-home-fixtures__score" aria-label={`Arsenal ${score.replace('–', ', opponent ')}`}>{score}</b>}
              <span className="rt-home-fixtures__date">{matchdayKickoffText(fixture)}</span>
              <small className="rt-home-fixtures__competition">{fixture.comp}</small>
              <span className="rt-home-fixtures__state">{state}</span>
              <span className="rt-home-fixtures__open">Open match <span aria-hidden="true">→</span></span>
            </button>
          </li>;
        })}
      </ol>
      {rows.length > 1 && <p className="rt-home-fixtures__hint">Swipe through fixtures and results <span aria-hidden="true">↔</span></p>}
    </> : <div className="rt-home-fixtures__empty"><p>No published fixtures are available in this copy. Arsenal’s official calendar has the latest dates.</p></div>}
    <footer className="rt-home-fixtures__sources">
      {source && <p>{sourceUrl ? <a href={sourceUrl} target="_blank" rel="noopener noreferrer">Data: {source.attributionLabel ?? source.label ?? source.provider}</a> : <span>Data: {source.attributionLabel ?? source.label ?? source.provider}</span>}{licenseUrl && <> · <a href={licenseUrl} target="_blank" rel="noopener noreferrer">{source.licenseLabel ?? 'Data licence'}</a></>}{source.provider === 'openligadb' && ' · Community maintained; checked time is not a match clock.'}</p>}
      <a href={OFFICIAL_CALENDAR} target="_blank" rel="noopener noreferrer">Official Arsenal calendar <span aria-hidden="true">↗</span></a>
    </footer>
  </section>;
}
